import React, { FunctionComponent, useContext, useMemo } from "react";
import {
    DetailsRowProps,
    ContentSection,
    Grid,
    GridRow,
    GridColumn,
    Stack,
    Checkbox,
    ActionButton,
    ListTable,
    ButtonRenderProps,
    ButtonProps,
    RenderFunction
} from "sprint-common-web";

import { TagElementsList } from "../../../../../common/components/TagElementsList";
import { renderNotMergableRow } from "../../../../common/utils/renderNotMergableRow";
import { TvSeriesDashboardContext } from "../../../TvSeriesDashboardProvider";
import { TvSeriesPageContext } from "../context/TvSeriesPageContext";
import { AddNewSeasonButton, AddNewSeasonButtonProps } from "./buttons/AddNewSeasonButton";
import { MergeSeasonsCommandBarButton } from "./buttons/MergeSeasonsCommandBarButton";
import { canSeasonsMerge } from "./canSeasonsMerge";
import { createSeasonLisingColumns } from "./createSeasonListingColumns";
import { SeasonListingItem } from "./SeasonListingItem";

export const SeasonListing: FunctionComponent = () => {
    const {
        listingItems,
        filters,
        isLoading,
        totalItems,
        seasonsToMerge,
        removeSeasonFromMergeList,
        setSort,
        setPage,
        setPageSize,
        setOrphanedOnly,
        clearFilters,
        setIncludeMerged
    } = useContext(TvSeriesPageContext);
    const { selectCurrentSeason } = useContext(TvSeriesDashboardContext);

    const handleSeasonSelected = (season?: SeasonListingItem) => {
        if (season) {
            selectCurrentSeason(season?.id, season?.name);
        }
    };
    const columns = useMemo(() => createSeasonLisingColumns(handleSeasonSelected), [handleSeasonSelected]);

    const handleOrphanedOnlyCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setOrphanedOnly(checked ? true : undefined);
    };

    const handleIncludeMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked);
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (isRowMergable(seasonsToMerge, rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }

        return renderNotMergableRow(rowProps, renderDefault);
    };

    return (
        <>
            <ContentSection
                isLoading={isLoading}
                header="Seasons"
                buttons={[mergeEpisodesButtonProps, createNewSeasonButtonProps]}
            >
                <Grid>
                    <GridRow style={{ margin: 0 }}>
                        <TagElementsList
                            items={seasonsToMerge}
                            onItemDismiss={removeSeasonFromMergeList}
                            renderItemText={getMergeSeasonText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow style={{ margin: 0 }}>
                        <GridColumn sm={2}>
                            <Stack styles={{ root: { paddingTop: 5 } }}>
                                <Checkbox
                                    label="Orphaned Only"
                                    onChange={handleOrphanedOnlyCheckboxChange}
                                    checked={filters.orphanedOnly ?? false}
                                />
                            </Stack>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack styles={{ root: { paddingTop: 5 } }}>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleIncludeMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </Stack>
                        </GridColumn>
                        <GridColumn sm={8}>
                            <Stack horizontalAlign="end">
                                <ActionButton
                                    iconProps={{ iconName: "ClearFilter" }}
                                    ariaLabel="Clear filters"
                                    label="Clear filters"
                                    onClick={clearFilters}
                                >
                                    Clear filters
                                </ActionButton>
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={listingItems}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isLoading}
                                onRenderRow={seasonsToMerge.length ? renderRow : undefined}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const isRowMergable = (episodesToMerge: SeasonListingItem[], rowItem?: SeasonListingItem) => {
    return (
        (canSeasonsMerge(episodesToMerge[0], rowItem) && episodesToMerge.length < 2) ||
        episodesToMerge.some((episode: SeasonListingItem) => episode.id === rowItem?.id)
    );
};

const createNewSeasonButtonProps: ButtonRenderProps<AddNewSeasonButtonProps> = {
    render: AddNewSeasonButton,
    isLabeled: true
};

const mergeEpisodesButtonProps: ButtonRenderProps<ButtonProps> = {
    render: MergeSeasonsCommandBarButton
};

const getMergeSeasonText = (listItem: SeasonListingItem) => {
    return `${listItem.name}`;
};
